import {
  blobToObjectUrl,
  fetchAuthenticatedBlob,
  saveOrShareBlob,
} from "../utils/authenticatedBlob";
import apiClient, { extractData } from "./client";

const ALLOWED_EXT = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
]);

const BLOCKED_EXT = new Set([
  ".exe",
  ".bat",
  ".cmd",
  ".apk",
  ".js",
  ".msi",
]);

export const EMPLOYEE_DOCUMENT_MAX_FILE_SIZE = 10 * 1024 * 1024;
export const EMPLOYEE_DOCUMENT_ACCEPT =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx";

export const EMPLOYEE_DOCUMENT_CATEGORIES = [
  { value: "NATIONAL_ID", label: "بطاقة الهوية", required: true },
  { value: "PERSONAL_PHOTO", label: "صورة شخصية", required: false },
  { value: "CONTRACT", label: "عقد العمل", required: true },
  { value: "EDUCATION", label: "مؤهل / شهادات دراسية", required: true },
  { value: "EXPERIENCE", label: "شهادات خبرة", required: false },
  { value: "SOCIAL_INSURANCE", label: "تأمينات اجتماعية", required: true },
  { value: "MEDICAL", label: "كشف طبي", required: true },
  { value: "BANK", label: "بيانات بنكية", required: true },
  { value: "OTHER", label: "أخرى", required: false },
];

/** تسميات الحقول اليدوية حسب فئة المستند */
export const DOCUMENT_FIELD_LABELS = {
  NATIONAL_ID: {
    documentNumber: "الرقم القومي",
    issuedAt: "تاريخ الإصدار",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "مثال: 29801011234567",
  },
  PERSONAL_PHOTO: {
    documentNumber: "رقم مرجعي",
    issuedAt: "تاريخ التصوير",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "اختياري",
  },
  CONTRACT: {
    documentNumber: "رقم العقد",
    issuedAt: "تاريخ بداية العقد",
    expiresAt: "تاريخ نهاية العقد",
    documentNumberPlaceholder: "رقم العقد",
  },
  EDUCATION: {
    documentNumber: "رقم الشهادة",
    issuedAt: "تاريخ الإصدار",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "رقم الشهادة / المؤهل",
  },
  EXPERIENCE: {
    documentNumber: "رقم الشهادة",
    issuedAt: "تاريخ الإصدار",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "رقم شهادة الخبرة",
  },
  SOCIAL_INSURANCE: {
    documentNumber: "رقم التأمينات",
    issuedAt: "تاريخ الاشتراك",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "رقم التأمين الاجتماعي",
  },
  MEDICAL: {
    documentNumber: "رقم الكشف / التقرير",
    issuedAt: "تاريخ الكشف",
    expiresAt: "تاريخ انتهاء الصلاحية",
    documentNumberPlaceholder: "رقم الكشف الطبي",
  },
  BANK: {
    documentNumber: "رقم الحساب / IBAN",
    issuedAt: "تاريخ الإصدار",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "رقم الحساب أو IBAN",
  },
  OTHER: {
    documentNumber: "رقم الوثيقة",
    issuedAt: "تاريخ الإصدار",
    expiresAt: "تاريخ الانتهاء",
    documentNumberPlaceholder: "رقم الوثيقة",
  },
};

export function getDocumentFieldLabels(category) {
  return (
    DOCUMENT_FIELD_LABELS[category] || DOCUMENT_FIELD_LABELS.OTHER
  );
}

export function getDocumentCategoryLabel(category) {
  return (
    EMPLOYEE_DOCUMENT_CATEGORIES.find((c) => c.value === category)?.label ||
    category
  );
}

export function getFileExtension(name = "") {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx).toLowerCase() : "";
}

export function validateEmployeeDocumentFile(file) {
  if (!file) return "لم يتم اختيار ملف";
  const ext = getFileExtension(file.name);
  if (BLOCKED_EXT.has(ext)) {
    return `نوع الملف محظور: ${ext}`;
  }
  if (!ALLOWED_EXT.has(ext)) {
    return "نوع الملف غير مسموح. المسموح: jpg, jpeg, png, pdf, doc, docx, xls, xlsx";
  }
  if (file.size > EMPLOYEE_DOCUMENT_MAX_FILE_SIZE) {
    return "حجم الملف يتجاوز الحد الأقصى (10 ميجابايت)";
  }
  return null;
}

export async function listEmployeeDocuments(employeeId) {
  const response = await apiClient.get(
    `/employees/${encodeURIComponent(employeeId)}/documents`,
  );
  return extractData(response);
}

export async function uploadEmployeeDocument(
  employeeId,
  { file, category, documentNumber, issuedAt, expiresAt, notes },
  onProgress,
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  if (documentNumber) formData.append("documentNumber", documentNumber);
  if (issuedAt) formData.append("issuedAt", issuedAt);
  if (expiresAt) formData.append("expiresAt", expiresAt);
  if (notes) formData.append("notes", notes);

  const response = await apiClient.post(
    `/employees/${encodeURIComponent(employeeId)}/documents`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded * 100) / event.total));
      },
    },
  );
  return extractData(response);
}

export async function updateEmployeeDocument(id, payload) {
  const response = await apiClient.patch(`/employee-documents/${id}`, payload);
  return extractData(response);
}

export async function deleteEmployeeDocument(id) {
  const response = await apiClient.delete(`/employee-documents/${id}`);
  return extractData(response);
}

export async function downloadEmployeeDocument(id, originalName) {
  const blob = await fetchAuthenticatedBlob(
    `/employee-documents/${id}/download`,
    "تعذر تحميل الملف",
  );
  await saveOrShareBlob(blob, originalName || "document");
}

export async function getEmployeeDocumentBlobUrl(id) {
  const blob = await fetchAuthenticatedBlob(
    `/employee-documents/${id}/download`,
    "تعذر عرض الملف",
  );
  return blobToObjectUrl(blob);
}
